import { motion, type HTMLMotionProps } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";
import type { CMSComponentProps } from "../engine/CMSComponent.tsx";
import RichText from "../engine/RichText.tsx";
import { isRichText, str, type PostSummary } from "../types.ts";
import { PostDate } from "./Blog.tsx";

/*
 * Home page blocks. Markup and classes are ported from shams-web
 * src/components/sections; content comes from the block fields.
 */

/** Fades content up once it scrolls into view. */
export const Reveal = (props: HTMLMotionProps<"div">) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    {...props}
  />
);

export function SectionShell({
  node,
  className = "py-16",
  children,
}: CMSComponentProps & { className?: string; children: ReactNode }) {
  return (
    <section id={str(node.anchorId) || undefined} className={className}>
      <div className="mx-auto max-w-3xl px-6">{children}</div>
    </section>
  );
}

export const SectionLabel = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <h2 className={`text-sm font-medium tracking-widest text-muted-foreground uppercase ${className}`}>
    {children}
  </h2>
);

const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

export function Hero({ node }: CMSComponentProps) {
  return (
    <SectionShell node={node} className="pt-32 pb-16 md:pt-40 md:pb-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {str(node.eyebrow) && <p className="text-sm text-muted-foreground">{str(node.eyebrow)}</p>}
        <h1 className="mt-3 text-4xl font-bold tracking-tight text-foreground md:text-5xl">
          {str(node.headline)}
        </h1>
        {isRichText(node.intro) && (
          <div className="mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground">
            <RichText document={node.intro} />
          </div>
        )}
      </motion.div>
    </SectionShell>
  );
}

export function About({ node }: CMSComponentProps) {
  return (
    <SectionShell node={node}>
      <Reveal className="space-y-4">
        <SectionLabel>{str(node.title)}</SectionLabel>
        {isRichText(node.body) && (
          <div className="space-y-4 text-[15px] leading-relaxed text-foreground/80">
            <RichText document={node.body} />
          </div>
        )}
      </Reveal>
    </SectionShell>
  );
}

export function Skills({ node }: CMSComponentProps) {
  const groups = list<{ label: string; items: string[] }>(node.groups);
  return (
    <SectionShell node={node}>
      <Reveal>
        <SectionLabel className="mb-6">{str(node.title)}</SectionLabel>
        <div className="space-y-5">
          {groups.map((group, index) => (
            <motion.div
              key={group.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4"
            >
              <span className="w-40 shrink-0 pt-1.5 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                {group.label}
              </span>
              <div className="flex flex-wrap gap-2">
                {group.items.map((skill) => (
                  <span
                    key={skill}
                    className="rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground/80"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </Reveal>
    </SectionShell>
  );
}

interface Project {
  name: string;
  description: string;
  tags: string[];
  url?: string;
}

export function Projects({ node }: CMSComponentProps) {
  const projects = list<Project>(node.projects);
  return (
    <SectionShell node={node}>
      <Reveal>
        <SectionLabel className="mb-8">{str(node.title)}</SectionLabel>
      </Reveal>

      <div className="space-y-6">
        {projects.map((project, index) => (
          <Reveal
            key={project.name}
            transition={{ delay: index * 0.1 }}
            className="group rounded-lg border border-border p-5 transition-colors hover:border-foreground/20"
          >
            <div className="flex items-start justify-between">
              <h3 className="font-semibold text-foreground">{project.name}</h3>
              {project.url && (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${project.name}`}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              )}
            </div>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{project.description}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {project.tags.map((tag) => (
                <span key={tag} className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        ))}
      </div>
    </SectionShell>
  );
}

/** Latest posts; the server adds `posts` to the block at request time. */
export function BlogPreview({ node }: CMSComponentProps) {
  const posts = list<PostSummary>(node.posts);
  if (posts.length === 0) return null;

  return (
    <SectionShell node={node}>
      <div className="mb-8 flex items-center justify-between">
        <SectionLabel>{str(node.title)}</SectionLabel>
        <a
          href="/posts"
          className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          {str(node.linkLabel) || "All posts"}
          <ArrowRight className="h-3 w-3" />
        </a>
      </div>

      <div className="space-y-4">
        {posts.map((post) => (
          <a
            key={post.slug}
            href={`/posts/${post.slug}`}
            className="group block rounded-lg border border-border p-5 transition-colors hover:border-foreground/20"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-foreground">{post.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{post.description}</p>
              </div>
              <p className="shrink-0 text-xs text-muted-foreground">
                <PostDate iso={post.publishedAt} />
              </p>
            </div>
          </a>
        ))}
      </div>
    </SectionShell>
  );
}

/** A heading and rich text, for simple pages (e.g. the torque calculator). */
export function TextSection({ node }: CMSComponentProps) {
  const isTitle = node.headingLevel === "h1";
  const Heading = isTitle ? "h1" : "h2";
  return (
    <section id={str(node.anchorId) || undefined} className={isTitle ? "pt-28" : "pb-16"}>
      <div className="mx-auto max-w-3xl px-6">
        {str(node.title) && (
          <Heading
            className={
              isTitle
                ? "text-4xl font-bold tracking-tight text-foreground"
                : "mt-8 text-2xl font-bold tracking-tight text-foreground"
            }
          >
            {str(node.title)}
          </Heading>
        )}
        {isRichText(node.body) && (
          <div className={isTitle ? "mt-2 text-lg text-muted-foreground" : "rich-text"}>
            <RichText document={node.body} />
          </div>
        )}
      </div>
    </section>
  );
}
